import React from 'react';
import { CircadianPhase, Signal } from '@demind-inc/core';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { circadianPhaseInfo } from '../../../data-access/helpers/constants';
import { formatTimeFromDayjs } from '../../../helpers';
import { useGeneralSettings } from '../../../hooks';
import './richContext.scss';

const ENERGY_ZONE_BODY_PREFIX = /^energy zone:\s*/i;

const parseEnergyZoneKey = (body: string): CircadianPhase | null => {
  const zoneKey = body.replace(ENERGY_ZONE_BODY_PREFIX, '').trim().toLowerCase();
  return zoneKey in circadianPhaseInfo ? (zoneKey as CircadianPhase) : null;
};

const SIGNAL_ICON: Record<string, string> = {
  energy: '⚡',
  sleep: '🌙',
  sleepsummary: '🌙',
  weather: '🌤',
  location: '📍',
  screen_time: '📱',
  commute: '🚇',
  menstrual_cycle: '🌸',
  steps: '👟',
  medication: '💊',
};

interface SignalPillProps {
  signal: Signal;
}

const getSleepSignalLabel = (signal: Signal, timeFormat: '12h' | '24h'): string | undefined => {
  const data =
    signal?.data && typeof signal.data === 'object' && !Array.isArray(signal.data)
      ? (signal.data as Record<string, unknown>)
      : null;
  if (!data) return signal.body || undefined;

  const history = Array.isArray(data.history) ? data.history : [];
  const firstHistory =
    history.length > 0 &&
    history[0] &&
    typeof history[0] === 'object' &&
    !Array.isArray(history[0])
      ? (history[0] as Record<string, unknown>)
      : null;
  const sleepTime =
    firstHistory && typeof firstHistory.sleep_time === 'string' ? firstHistory.sleep_time : null;
  const wakeTime =
    firstHistory && typeof firstHistory.wake_time === 'string' ? firstHistory.wake_time : null;
  if (sleepTime) {
    const sleepParsed = dayjs(sleepTime);
    const wakeParsed = wakeTime ? dayjs(wakeTime) : null;
    if (sleepParsed.isValid()) {
      const dateText = sleepParsed.format('MMM D, YYYY');
      const shortDateText = sleepParsed.format('MMM D');
      const startText = formatTimeFromDayjs(sleepParsed, timeFormat);
      if (wakeParsed?.isValid()) {
        const endText = formatTimeFromDayjs(wakeParsed, timeFormat);
        return `${shortDateText}: ${startText} - ${endText}`;
      }
      return `${shortDateText}: ${startText}`;
    }
    return `Sleep: ${sleepTime}${wakeTime ? ` - ${wakeTime}` : ''}`;
  }

  const sleepDate = typeof data.sleep_date === 'string' ? data.sleep_date : null;
  if (sleepDate) {
    const parsed = dayjs(sleepDate);
    return parsed.isValid() ? `Sleep on ${parsed.format('MMM D, YYYY')}` : `Sleep on ${sleepDate}`;
  }

  return signal.body || undefined;
};

const SignalPill: React.FC<SignalPillProps> = ({ signal }) => {
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const source = typeof signal.signal_source === 'string' ? signal.signal_source.trim() : '';
  const normalizedSource = source.toLowerCase();
  const icon = SIGNAL_ICON[normalizedSource] ?? SIGNAL_ICON[source] ?? '●';
  const isSleep = normalizedSource === 'sleep' || normalizedSource === 'sleepsummary';
  const sourceLabel =
    normalizedSource === 'sleepsummary'
      ? 'sleep summary'
      : (source || String(signal.signal_source)).replace(/_/g, ' ');
  const bodyLabel = (() => {
    if (isSleep) {
      return getSleepSignalLabel(signal, timeFormat ?? '24h');
    }
    if (!signal.body) return undefined;
    if (normalizedSource !== 'energy') return signal.body;

    const zoneKey = parseEnergyZoneKey(signal.body);
    return zoneKey ? circadianPhaseInfo[zoneKey].title : signal.body;
  })();

  return (
    <div
      className={clsx('rc-signal-pill', {
        'rc-signal-pill--urgent': signal.urgency === 'now',
        'rc-signal-pill--sleep': isSleep,
      })}
    >
      <span className={clsx('rc-signal-icon', { 'rc-signal-icon--sleep': isSleep })}>{icon}</span>
      <div>
        <div className={clsx('rc-signal-source', { 'rc-signal-source--sleep': isSleep })}>
          {sourceLabel}
        </div>
        {!!bodyLabel && (
          <div className={clsx('rc-signal-body', { 'rc-signal-body--sleep': isSleep })}>
            {bodyLabel}
          </div>
        )}
      </div>
    </div>
  );
};

export default SignalPill;
